

"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { Card, Row, Col, Input, Button, Form, Modal, ModalBody, Table, Tooltip } from "reactstrap"
import { FaPlus, FaCheck, FaTrash, FaArrowLeft, FaEdit, FaBoxOpen, FaClipboardList } from "react-icons/fa"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import LoadingOverlay from "react-loading-overlay-ts"
import DNALoader from "../../components/Loaders/DnaLoader"
import Alerts from "../../components/Alerts/alerts"
import DashboardService from "./dashboard.service"
import AuthService from "../Authentication/auth.service"
import { useTranslation } from "react-i18next"
import Select from "react-select"
import moment from "moment"
import ItemPickerModal from "./ItemPickerModal"

const OrderConfirmationModal = ({ isOpen, toggle, orderId }) => {
  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="order-confirmation-modal">
      <ModalBody className="text-center p-5">
        <div className="confirmation-icon mb-4">
          <FaCheck size={28} color="#fff" />
        </div>
        <h3 className="mb-3 font-weight-bold">Order Placed Successfully</h3>
        <div className="order-id-container p-3 mb-3">
          <h2 className="order-id mb-0">ID# {orderId}</h2>
        </div>
        <p className="text-muted mb-0">Your order has been submitted and is being processed.</p>
      </ModalBody>
    </Modal>
  )
}

// New component for batch selection modal
const BatchSelectionModal = ({ isOpen, toggle, availableBatches, selectedBatches, onSave, totalQuantity }) => {
  const { t } = useTranslation()
  const [batchSelections, setBatchSelections] = useState([])
  const [remainingQuantity, setRemainingQuantity] = useState(totalQuantity)
  const [error, setError] = useState("")

  useEffect(() => {
    // Initialize batch selections from previously selected batches or empty array
    const initialSelections = selectedBatches?.length > 0 ? [...selectedBatches] : []

    setBatchSelections(initialSelections)

    // Calculate remaining quantity
    const selectedTotal = initialSelections.reduce((sum, batch) => sum + batch.quantity, 0)
    setRemainingQuantity(totalQuantity - selectedTotal)
  }, [isOpen, availableBatches, selectedBatches, totalQuantity])

  const handleQuantityChange = (batchId, newQuantity) => {
    // Find the batch in available batches to get max quantity
    const availableBatch = availableBatches.find((b) => b._id === batchId)
    const maxAvailable = availableBatch ? availableBatch.quantity : 0

    // Ensure quantity doesn't exceed available
    newQuantity = Math.min(Number.parseInt(newQuantity) || 0, maxAvailable)

    // Update batch selections
    const updatedSelections = [...batchSelections]
    const existingIndex = updatedSelections.findIndex((b) => b.batchId === batchId)

    if (existingIndex >= 0) {
      // Update existing selection
      const oldQuantity = updatedSelections[existingIndex].quantity
      updatedSelections[existingIndex].quantity = newQuantity

      // If quantity is 0, remove the batch
      if (newQuantity === 0) {
        updatedSelections.splice(existingIndex, 1)
      }

      // Update remaining quantity
      setRemainingQuantity((prev) => prev + (oldQuantity - newQuantity))
    } else if (newQuantity > 0) {
      // Add new selection
      updatedSelections.push({
        batchId: batchId,
        batch_number: availableBatch.batch_number,
        quantity: newQuantity,
        expiry: availableBatch.expiry_date,
      })

      // Update remaining quantity
      setRemainingQuantity((prev) => prev - newQuantity)
    }

    setBatchSelections(updatedSelections)

    // Clear error if selections are valid
    if (updatedSelections.reduce((sum, batch) => sum + batch.quantity, 0) === totalQuantity) {
      setError("")
    }
  }

  const handleSave = () => {
    const selectedTotal = batchSelections.reduce((sum, batch) => sum + batch.quantity, 0)

    if (selectedTotal !== totalQuantity) {
      setError(t("Selected batch quantities must equal the total quantity needed"))
      return
    }

    onSave(batchSelections)
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="batch-selection-modal">
      <ModalBody className="p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <div className="modal-icon-container me-3">
              <FaBoxOpen size={24} />
            </div>
            <h4 className="mb-0 fw-bold">{t("Select Batches")}</h4>
          </div>
          <button type="button" className="btn-close" onClick={toggle} aria-label="Close"></button>
        </div>

        <div className="batch-selection-summary p-3 mb-4">
          <div className="row">
            <div className="col-md-6">
              <div className="summary-item">
                <span className="summary-label">{t("Total Quantity Needed")}:</span>
                <span className="summary-value">{totalQuantity}</span>
              </div>
            </div>
            <div className="col-md-6">
              <div className="summary-item">
                <span className="summary-label">{t("Remaining to Allocate")}:</span>
                <span className={`summary-value ${remainingQuantity > 0 ? "text-danger" : "text-success"}`}>
                  {remainingQuantity}
                </span>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger d-flex align-items-center mb-4">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        <div className="batch-table-container">
          <Table bordered responsive hover className="batch-table">
            <thead>
              <tr>
                <th>{t("Batch Number")}</th>
                <th>{t("Available Quantity")}</th>
                <th>{t("Expiry Date")}</th>
                <th>{t("Select Quantity")}</th>
              </tr>
            </thead>
            <tbody>
              {availableBatches
                ?.filter((batch) => batch.quantity > 0)
                .sort((a, b) => {
                  const aDate = a.expiry_date ? new Date(a.expiry_date) : new Date(8640000000000000)
                  const bDate = b.expiry_date ? new Date(b.expiry_date) : new Date(8640000000000000)
                  return aDate - bDate
                })
                .map((batch) => {
                  const selectedBatch = batchSelections.find((b) => b.batchId === batch._id)
                  const selectedQuantity = selectedBatch ? selectedBatch.quantity : 0

                  return (
                    <tr key={batch._id}>
                      <td className="batch-number">{batch.batch_number}</td>
                      <td className="available-quantity">{batch.quantity}</td>
                      <td className="expiry-date">{batch.expiry_date ? moment(batch.expiry_date).format("DD-MM-YYYY") : ''}</td>
                      <td className="select-quantity">
                        <Input
                          type="text" // Change from "number" to "text" to allow empty input
                          value={selectedQuantity === 0 ? "" : selectedQuantity} // Show empty if 0
                          onChange={(e) => {
                            const newValue = e.target.value;
                            if (newValue === "" || /^[0-9\b]+$/.test(newValue)) {
                              handleQuantityChange(batch._id, newValue === "" ? 0 : parseInt(newValue, 10));
                            }
                          }}
                          className="quantity-input"
                        />

                        <div className="progress mt-2" style={{ height: "6px" }}>
                          <div
                            className="progress-bar bg-primary"
                            role="progressbar"
                            style={{ width: `${(selectedQuantity / totalQuantity) * 100}%` }}
                            aria-valuenow={selectedQuantity}
                            aria-valuemin="0"
                            aria-valuemax={totalQuantity}
                          ></div>
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-end mt-4">
          <Button color="light" className="me-2 px-4" onClick={toggle}>
            {t("Cancel")}
          </Button>
          <Button color="primary" className="px-4" onClick={handleSave} disabled={remainingQuantity !== 0}>
            <FaCheck className="me-2" />
            {t("Save Selections")}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

// ─── Custom Option with hover image preview ───────────────────────────────────
const ItemOption = (props) => {
  const { data, innerRef, innerProps, isFocused, isSelected, isDisabled } = props
  const [preview, setPreview] = useState(null) // { x, y }
  const optionRef = useRef(null)

  const handleMouseEnter = (e) => {
    if (isDisabled || !data.Images?.[0]) return
    const rect = e.currentTarget.getBoundingClientRect()
    setPreview({ top: rect.top, left: rect.right + 8 })
  }

  const handleMouseLeave = () => setPreview(null)

  return (
    <>
      <div
        ref={innerRef}
        {...innerProps}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{
          padding: '8px 12px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          backgroundColor: isSelected ? '#3D607A' : isFocused ? 'rgba(61,96,122,0.1)' : 'transparent',
          color: isDisabled ? '#a0a0a0' : isSelected ? '#fff' : '#333',
          opacity: isDisabled ? 0.5 : 1,
          fontSize: '0.875rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {/* Tiny inline thumbnail */}
        {data.Images?.[0] ? (
          <img
            src={data.Images[0]}
            alt=""
            style={{ width: 24, height: 24, borderRadius: 4, objectFit: 'cover', flexShrink: 0, border: '1px solid #ddd' }}
          />
        ) : (
          <span style={{ width: 24, height: 24, borderRadius: 4, background: '#e8ecf0', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: '#bbb' }}>
            <i className="fas fa-pills" />
          </span>
        )}
        <span style={{ flex: 1 }}>{data.label}</span>
      </div>

      {/* Floating preview card rendered via portal into body */}
      {preview && (
        <div
          style={{
            position: 'fixed',
            top: Math.min(preview.top, window.innerHeight - 220),
            left: preview.left,
            zIndex: 9999,
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            width: 180,
            overflow: 'hidden',
            pointerEvents: 'none',
          }}
        >
          <div style={{ width: '100%', aspectRatio: '1', background: 'linear-gradient(135deg,#f5f7fa,#e8ecf0)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
            <img src={data.Images[0]} alt={data.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <div style={{ padding: '10px 12px 12px' }}>
            <p style={{ margin: 0, fontWeight: 600, fontSize: '0.8rem', color: '#333', lineHeight: 1.3 }}>{data.label}</p>
            <div style={{ display: 'flex', gap: 4, marginTop: 6, flexWrap: 'wrap' }}>
              {data.item_code && <span style={{ fontSize: '0.68rem', background: '#f0f4f7', color: '#3D607A', borderRadius: 20, padding: '2px 7px', fontWeight: 500 }}>{data.item_code}</span>}
              {data.BaseUOM && <span style={{ fontSize: '0.68rem', background: 'rgba(61,96,122,0.12)', color: '#3D607A', borderRadius: 20, padding: '2px 7px', fontWeight: 500 }}>{data.BaseUOM}</span>}
            </div>
            {data.price > 0 && <p style={{ margin: '6px 0 0', fontSize: '0.8rem', color: '#2d7a4a', fontWeight: 700 }}>RM {Number(data.price).toFixed(2)}</p>}
          </div>
        </div>
      )}
    </>
  )
}

// ─── Selected-item hover preview icon (sits outside the dropdown) ───────────
const SelectedItemPreviewIcon = ({ selectedItem }) => {
  const [hover, setHover] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })
  const iconRef = useRef(null)

  if (!selectedItem) return null

  const handleMouseEnter = () => {
    if (!iconRef.current) return
    const rect = iconRef.current.getBoundingClientRect()
    setPos({
      top: rect.top,
      left: rect.right + 8,
    })
    setHover(true)
  }

  const handleMouseLeave = () => setHover(false)

  return (
    <>
      <button
        ref={iconRef}
        type="button"
        className="rfq-preview-icon-btn"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        aria-label={`Preview ${selectedItem.label}`}
        title={selectedItem.label}
      >
        {selectedItem.Images?.[0] ? (
          <img
            src={selectedItem.Images[0]}
            alt={selectedItem.label}
          />
        ) : (
          <i className="fas fa-pills" />
        )}
      </button>

      {hover && (
        <div
          className="rfq-preview-popover"
          style={{ top: Math.min(pos.top, window.innerHeight - 240), left: pos.left, pointerEvents: 'none' }}
        >
          <div className="rfq-preview-popover__media">
            {selectedItem.Images?.[0] ? (
              <img
                src={selectedItem.Images[0]}
                alt={selectedItem.label}
              />
            ) : (
              <i className="fas fa-pills" />
            )}
          </div>
          <div className="rfq-preview-popover__body">
            <p className="rfq-preview-popover__title">{selectedItem.label}</p>
            <div className="rfq-preview-popover__chips">
              {selectedItem.item_code && (
                <span className="rfq-preview-chip rfq-preview-chip--code">
                  {selectedItem.item_code}
                </span>
              )}
              {selectedItem.BaseUOM && (
                <span className="rfq-preview-chip rfq-preview-chip--uom">
                  {selectedItem.BaseUOM}
                </span>
              )}
            </div>
            {selectedItem.price > 0 && (
              <p className="rfq-preview-popover__price">
                RM {Number(selectedItem.price).toFixed(2)}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  )
}

const AddNewRFQ = () => {
  const [items, setItems] = useState([])
  const [allItems, setAllItems] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [orderId, setOrderId] = useState("")
  const [loading, setLoading] = useState(false)
  const [selectedItems, setSelectedItems] = useState(new Set())
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [userInfo] = useState(JSON.parse(localStorage.getItem("user")))
  const [Fetched_Businesses, setFetched_Businesses] = useState([])
  const location = useLocation()
  const orderDetails = location.state?.orderDetails
  const [allDoctors, setAllDoctors] = useState([])
  const [doctor, setDoctor] = useState("")
  const [reason_of_use, setReason_of_use] = useState("")
  const [subtotal, setSubtotal] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)
  const [tooltipOpen, setTooltipOpen] = useState({})
  const [payment_terms, setPaymentTerms] = useState('advance')

  // New state for batch selection modal
  const [batchModalOpen, setBatchModalOpen] = useState(false)
  const [currentItemIndex, setCurrentItemIndex] = useState(null)

  // Item picker modal state
  const [itemPickerOpen, setItemPickerOpen] = useState(false)
  const [itemPickerIndex, setItemPickerIndex] = useState(null)

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: state.isFocused ? "#3D607A" : "#e0e0e0",
      boxShadow: state.isFocused ? "0 0 0 1px #3D607A" : "none",
      "&:hover": {
        borderColor: "#3D607A",
      },
      borderRadius: "8px",
      padding: "2px",
    }),
    menu: (provided) => ({
      ...provided,
      width: "420px",
    }),
    singleValue: (provided) => ({
      ...provided,
      maxWidth: "none",
      width: "auto",
    }),
    input: (provided) => ({
      ...provided,
      minWidth: "120px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      flexWrap: "nowrap",
      overflow: "hidden",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3D607A"
        : state.isFocused
          ? "rgba(61, 96, 122, 0.1)"
          : null,
      color: state.isDisabled ? "#a0a0a0" : state.isSelected ? "white" : "#333", // Fade color for disabled items
      opacity: state.isDisabled ? 0.5 : 1, // Reduce opacity for disabled items
      cursor: state.isDisabled ? "not-allowed" : "pointer", // Change cursor for disabled items
      "&:active": {
        backgroundColor: "#3D607A",
      },
    }),
  };

  useEffect(() => {
    const calculatedSubtotal = items.reduce((sum, item) => {
      return sum + calculateItemTotal(item)
    }, 0)
    setSubtotal(calculatedSubtotal)
    setTotalAmount(calculatedSubtotal)
  }, [items])

  useEffect(() => {
    fetchAllItems()
    Fetch_Businesses()
    fetchAllDoctors()
  }, [])

  useEffect(() => {
    if (allItems.length > 0 && orderDetails) {
      prefillOrderDetails()
    }
  }, [allItems, orderDetails])

  function Fetch_Businesses() {
    setLoading(true)
    DashboardService.GetBusinesses().then(
      (response) => {
        setLoading(false)
        if (response.data.message === "Unauthorized") {
          AuthService.logout()
          navigate("/login")
          window.location.reload()
        } else {
          const business = response.data.data[0]
          setFetched_Businesses([business])
        }
      },
      (error) => {
        setLoading(false)
        const errContent = (error.response && error.response.data) || error.message || error.toString()
        Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."))
      },
    )
  }

  const fetchAllItems = async () => {
    setLoading(true)
    try {
      const getData = {
        functionName: "GetDropDownItems",
        postData: {
          sort: { _id: -1 },
          offset: 0,
          limit: 99999,
          portal: "customers",
          filter: {},
          business: [],
        },
      }
      const response = await DashboardService.getAllItems(getData)
      if (response.data.message === "Unauthorized") {
        AuthService.logout()
        navigate("/login")
      } else {
        // Add formatted label to each item
        const itemsWithFormattedLabel = response.data.data.map((item) => ({
          ...item,
          formattedLabel: `${item.item_name} ${item.BaseUOM} (${item.ItemGroup || "NP"})`,
        }))
        setAllItems(itemsWithFormattedLabel)
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching items"))
    }
    setLoading(false)
  }

  const fetchAllDoctors = async () => {
    setLoading(true)
    try {
      const getData = {
        functionName: "GetAllDoctors",
        postData: {},
      }
      const response = await DashboardService.GetAllDoctors(getData)
      if (response.data.message === "Unauthorized") {
        AuthService.logout()
        navigate("/login")
      } else {
        setAllDoctors(response.data.data)
      }
    } catch (error) {
      Alerts.swalErrorAlert(t("Error fetching items"))
    }
    setLoading(false)
  }



  const handleAddItem = () => {
    setItems([
      ...items,
      {
        name: null,
        quantity: 1,
        availableBatches: [],
        selectedBatches: [],
        price: 0,
        discount: 0,
        discountType: 'percent',
      },
    ])
  }
  const calculateItemTotal = (item) => {
    const baseTotal = (item.price || 0) * (item.quantity || 0)
    const discountValue = Number.parseFloat(item.discount || 0)
    if (!discountValue) return baseTotal
    if (item.discountType === 'amount') {
      const capped = Math.min(discountValue, baseTotal)
      return baseTotal - capped
    }
    const percent = Math.min(Math.max(discountValue, 0), 100)
    const discountAmount = baseTotal * (percent / 100)
    return baseTotal - discountAmount
  }

  const fetchBatches = async (item_code) => {
    try {
      setLoading(true)
      const response = await DashboardService.GetStocks(item_code)
      setLoading(false)
      //iph todo: 47
      //console.log("Fetched batches:", response)
      return response.data || []
    } catch (error) {
      setLoading(false)
      console.error("Error fetching batches:", error)
      Alerts.swalErrorAlert(t("Error fetching batches"))
      return []
    }
  }

  const handleItemChange = async (index, key, value) => {
    //iph todo: 47
    //console.log("index, key, value", index, key, value)
    const newItems = [...items]
    const oldItem = newItems[index]
    newItems[index] = { ...oldItem, [key]: value }

    if (key === "name") {
      newItems[index] = {
        ...oldItem,
        name: value,
        category: value.category,
        price: value.price || 0,
        discount: 0, // you can set default discount if needed
        selectedBatches: [],
      }

      try {
        const batchData = await fetchBatches(value.item_code)
        newItems[index].availableBatches = batchData.data
      } catch (error) {
        newItems[index].availableBatches = []
      }

      setSelectedItems((prev) => {
        const updated = new Set(prev)
        if (oldItem.name) {
          updated.delete(oldItem.name)
        }
        if (value) {
          updated.add(value)
        }
        return updated
      })
    }


    // if (key === "name") {
    //   newItems[index].category = value.category
    //   newItems[index].selectedBatches = [] // Reset selected batches when item changes
    //   console.log("value", value)

    //   try {
    //     const batchData = await fetchBatches(value.item_code)
    //     console.log("batchData", batchData)
    //     newItems[index].availableBatches = batchData.data // Store available batches
    //   } catch (error) {
    //     console.error("Error fetching batches:", error)
    //     newItems[index].availableBatches = []
    //   }

    //   setSelectedItems((prev) => {
    //     const updated = new Set(prev)
    //     if (oldItem.name) {
    //       updated.delete(oldItem.name)
    //     }
    //     if (value) {
    //       updated.add(value)
    //     }
    //     return updated
    //   })
    // }

    setItems(newItems)
  }

  const handleDoctor = (selectedOption) => {
    setDoctor(selectedOption)
  }

  const handleRemoveItem = (index) => {
    const removedItem = items[index]
    const newItems = items.filter((_, i) => i !== index)
    setItems(newItems)

    if (removedItem.name) {
      setSelectedItems((prev) => {
        const updated = new Set(prev)
        updated.delete(removedItem.name)
        return updated
      })
    }
  }

  const toggleTooltip = (index) => {
    setTooltipOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }))
  }

  const prefillOrderDetails = async () => {
    if (orderDetails && orderDetails.items) {
      const prefillItems = await Promise.all(
        orderDetails.items.map(async (item) => {
          const prefillItem = {
            category: item.category,
            name: item.item,
            quantity: item.quantity,
            ItemGroup: item.ItemGroup,
            status: item.status,
            availableBatches: [],
            selectedBatches: [],
          }

          if (item.item && item.item.value) {
            try {
              const batchData = await fetchBatches(item.item.item_code)
              prefillItem.availableBatches = batchData.data
            } catch (error) {
              console.error("Error fetching batches for prefill:", error)
            }
          }

          return prefillItem
        }),
      )

      setItems(prefillItems)

      const newSelectedItems = new Set(prefillItems.map((item) => item.name))
      setSelectedItems(newSelectedItems)
      setReason_of_use(orderDetails.reason_of_use)
    }
  }

  const saveRFQData = async (rfqData) => {
    try {
      const response = await DashboardService.AddOrderByAdmin(rfqData)
      if (response.data.message === "Unauthorized") {
        AuthService.logout()
        navigate("/login")
      } else {
        return response.data
      }
    } catch (error) {
      console.error("Error saving Order:", error)
      throw error
    }
  }

  const openBatchSelectionModal = (index) => {
    setCurrentItemIndex(index)
    setBatchModalOpen(true)
  }

  const openItemPicker = (index) => {
    setItemPickerIndex(index)
    setItemPickerOpen(true)
  }

  const handleBatchSelectionSave = (selectedBatches) => {
    if (currentItemIndex !== null) {
      const newItems = [...items]
      newItems[currentItemIndex].selectedBatches = selectedBatches
      setItems(newItems)
    }
  }

  const getTotalSelectedQuantity = (item) => {
    return item.selectedBatches?.reduce((total, batch) => total + batch.quantity, 0) || 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (doctor === "") {
      return Alerts.swalErrorAlert(t("Please Select a doctor"))
    }
    if (items.length === 0) {
      return Alerts.swalErrorAlert(t("At least select 1 item."))
    }

    // Validate that all items have batches selected with correct quantities
    const invalidItems = items.filter((item) => {
      if (!item.name) return false
      const totalSelected = getTotalSelectedQuantity(item)
      return totalSelected !== Number.parseInt(item.quantity)
    })

    if (invalidItems.length > 0) {
      return Alerts.swalErrorAlert(t("Please select batches with correct quantities for all items"))
    }

    setLoading(true)
    try {
      const RFQ_ID = Math.floor(100000000 + Math.random() * 900000000)

      const rfqData = {
        // items: items.map((item) => ({
        //   category: item.category,
        //   item: item.name.value,
        //   quantity: Number.parseInt(item.quantity),
        //   ItemGroup: item.name.ItemGroup,
        //   status: item.name.status,
        //   selectedBatches: item.selectedBatches.map((batch) => ({
        //     batchId: batch.batchId,
        //     batch_number: batch.batch_number,
        //     quantity: batch.quantity,
        //   })),
        // })),
        items: items.map((item) => ({
          category: item.category,
          item: item.name.value,
          quantity: Number.parseInt(item.quantity),
          ItemGroup: item.name.ItemGroup,
          status: item.name.status,
          price: Number.parseFloat(item.price),
          discount: Number.parseFloat(item.discount || 0),
          discount_type: item.discountType === 'amount' ? 'amount' : 'percent',
          item_total: calculateItemTotal(item),
          selectedBatches: item.selectedBatches.map((batch) => ({
            batchId: batch.batchId,
            batch_number: batch.batch_number,
            quantity: batch.quantity,
          })),
        })),
        subtotal: subtotal.toFixed(2),
        total_amount: totalAmount.toFixed(2),
        user_id: doctor.value,
        reason_of_use: 'Treatment of Patient',
        paymentTerms: payment_terms,
        rfq_id: RFQ_ID,
        business_id: Fetched_Businesses[0]._id,
        status: "accepted",
      }

      await saveRFQData(rfqData)
      
      setOrderId(RFQ_ID)
      setModalOpen(true)
      
      // Show success message
      Alerts.swalSuccessAlert(`Order #${RFQ_ID} received successfully!`)
      
      // After showing the confirmation, navigate after a delay
      setTimeout(() => {
        navigate("/Quotations")
      }, 3000)
    } catch (error) {
      Alerts.swalErrorAlert(t("Error submitting Order"))
    } finally {
      setLoading(false)
    }
  }

  // Helper to go back to correct page
  const goBackAfterEdit = () => {
    if (location.state?.from === 'quotations') {
      navigate('/quotations');
    } else if (location.state?.from === 'request_for_quotation') {
      navigate('/request_for_quotation');
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="app-main__outer">
      <div className="container mt-4">
        <div className="mb-4">
          <Button color="link" className="p-0 text-decoration-none" onClick={goBackAfterEdit}>
            <FaArrowLeft className="me-2" />
            <span className="text-dark fw-bold">{t("New Order")}</span>
          </Button>
        </div>
        <Card className="rfq-card">
          <div className="p-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h4 className="mb-0 fw-bold">
                <FaClipboardList className="me-2 text-primary" />
                {orderDetails ? t("Re New Order") : t("Add New Order")}
              </h4>
            </div>
            <Form onSubmit={handleSubmit}>
              <LoadingOverlay active={loading} spinner={<DNALoader />}>
                {/* <div className="doctor-selection-container p-4 mb-4">
                  <h5 className="mb-3 fw-bold section-title">
                    <i className="fas fa-user-md me-2"></i>
                    {t("Select Doctor")}
                  </h5>
                  <Row>
                    <Col md={6}>
                      <Select
                        value={doctor}
                        onChange={(selectedOption) => handleDoctor(selectedOption)}
                        options={allDoctors.map((doc) => ({
                          value: doc._id,
                          label: doc.name,
                        }))}
                        placeholder={t("Select Doctor")}
                        isSearchable
                        styles={selectStyles}
                        className="doctor-select"
                      />
                    </Col>
                  </Row>
                </div> */}
                <div className="doctor-selection-container p-4 mb-4">
                  <Col md={12}>
                    <Row className="mb-3 align-items-center">
                      <Col md={2}>
                        <h5 className="mb-3 fw-bold section-title">
                          <i className="fas fa-user-md me-2"></i>
                          {t("Select Doctor")}
                        </h5>
                      </Col>
                      <Col md={4}>
                        <Select
                          value={doctor}
                          onChange={(selectedOption) => handleDoctor(selectedOption)}
                          options={allDoctors.map((doc) => ({
                            value: doc._id,
                            label: doc.name,
                          }))}
                          placeholder={t("Select Doctor")}
                          isSearchable
                        />
                      </Col>
                    </Row>
                    <Row className="mb-3 align-items-center">
                      <Col md={2}>
                        <h5 className="mb-3 fw-bold section-title">
                          <i className="fas fa-file-invoice-dollar me-2"></i>
                          {t("Credit Term")}
                        </h5>
                      </Col>
                      <Col md={4}>
                        <Input
                          type="select"
                          value={payment_terms}
                          onChange={(e) => setPaymentTerms(e.target.value)}
                        >
                          <option value="advance">{t("Advance / C.O.D.")}</option>
                          <option value="net-30">{t("Net 30")}</option>
                          <option value="net-60">{t("Net 60")}</option>
                          <option value="net-90">{t("Net 90")}</option>
                        </Input>
                      </Col>
                    </Row>
                    {/**
                     * Reason of Use field hidden (hard-coded in submission)
                     * Uncomment if you need to show editable field again
                     */}
                    {false && (
                      <Row className="mb-3 align-items-center">
                        <Col md={2}>
                          <h5 className="mb-3 fw-bold section-title">
                            <i className="fas fa-user-md me-2"></i>
                            {t("Reason of Use")}
                          </h5>
                        </Col>
                        <Col md={4}>
                          <Input
                            type="text"
                            value={reason_of_use}
                            onChange={(e) => setReason_of_use(e.target.value)}
                          />
                        </Col>
                      </Row>
                    )}
                  </Col>
                </div>

                <div className="items-container p-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 fw-bold section-title">
                      <i className="fas fa-shopping-cart me-2"></i>
                      {t("Add Item(s)")}
                    </h5>
                  </div>

                  <div className="item-list mb-4">
                    <div className="item-header mb-3">
                      <Row className="text-muted fw-bold text-center">
                        <Col md={2}>{t("Item(s)")}</Col>
                        <Col md={1}>{t("Quantity")}</Col>
                        <Col md={1}>{t("UOM")}</Col>
                        <Col md={2}>{t("Batches")}</Col>
                        <Col md={1}>{t("Price (RM)")}</Col>
                        <Col md={2}>{t("Discount")}</Col>
                        <Col md={1}>{t("Total")}</Col>
                        <Col md={1}>{t("Status")}</Col>
                        <Col md={1}>{t("Action")}</Col>

                      </Row>
                    </div>

                    {items.length === 0 ? (
                      <div className="empty-items-message p-4 text-center">
                        <div className="empty-icon mb-3">
                          <i className="fas fa-box-open fa-3x text-muted"></i>
                        </div>
                        <p className="text-muted mb-0">{t("No items added yet. Click 'Add New Item' to start.")}</p>
                      </div>
                    ) : (
                      items.map((item, index) => {
                        const totalSelected = getTotalSelectedQuantity(item)
                        const isQuantityMatched = totalSelected === Number.parseInt(item.quantity)

                        return (
                          <div key={index} className="item-row p-3 mb-3">
                            <Row className="align-items-center">
                              {/* Item Selection */}
                              <Col md={2}>
                                <div id={`tooltip-${index}`} className="rfq-select-wrap">
                                  <SelectedItemPreviewIcon selectedItem={item.name} />
                                  <Select
                                    value={item.name}
                                    onChange={(selectedOption) => handleItemChange(index, "name", selectedOption)}
                                    options={allItems.map((catItem) => ({
                                      category: catItem.category,
                                      value: catItem._id,
                                      label: catItem.item_name,
                                      ItemGroup: catItem.ItemGroup,
                                      item_code: catItem.item_code,
                                      price: catItem.price,
                                      BaseUOM: catItem.BaseUOM,
                                      Images: catItem.Images,
                                      isDisabled:
                                        catItem.status !== "Active"
                                    }))}
                                    placeholder={t("Select Item")}
                                    isSearchable
                                    styles={{
                                      ...selectStyles,
                                      control: (provided, state) => ({
                                        ...provided,
                                        borderColor: state.isFocused ? "#3D607A" : "#e0e0e0",
                                        boxShadow: state.isFocused ? "0 0 0 1px #3D607A" : "none",
                                        borderRadius: "8px",
                                        padding: "2px",
                                        paddingLeft: "36px",
                                        minHeight: "38px",
                                      }),
                                    }}
                                    components={{ Option: ItemOption }}
                                  />
                                  <SelectedItemPreviewIcon selectedItem={item.name} />
                                </div>
                                {/* <button
                                  type="button"
                                  className="ipm-browse-btn mt-1"
                                  onClick={() => openItemPicker(index)}
                                  title={t("Browse with images")}
                                >
                                  <i className="fas fa-th-large me-1" />
                                  {t("Browse")}
                                </button> */}
                                {item.name && (
                                  <Tooltip
                                    placement="top"
                                    isOpen={tooltipOpen[index] || false}
                                    target={`tooltip-${index}`}
                                    toggle={() => toggleTooltip(index)}
                                  >
                                    {allItems.find(catItem => catItem._id === item.name.value)?.formattedLabel || item.name.label}
                                  </Tooltip>
                                )}
                              </Col>

                              {/* Quantity Selection */}
                              <Col md={1}>
                                <Input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleItemChange(index, "quantity", Number.parseInt(e.target.value) || 1)
                                  }
                                  className="quantity-input"
                                />
                              </Col>

                              {/* UOM Display */}
                              <Col md={1}>
                                <Input
                                  type="text"
                                  value={item.name?.BaseUOM || ''}
                                  readOnly
                                  className="form-control-plaintext"
                                />
                              </Col>

                              {/* Batch Selection Button */}
                              <Col md={2}>
                                <Button
                                  color={item.selectedBatches?.length > 0 ? "outline-success" : "outline-primary"}
                                  size="sm"
                                  className="w-100 batch-select-btn"
                                  onClick={() => openBatchSelectionModal(index)}
                                  disabled={!item.name || !item.availableBatches?.length}
                                >
                                  <FaEdit className="me-1" />
                                  {item.selectedBatches?.length > 0
                                    ? t("Edit Selected Batches", { count: item.selectedBatches.length })
                                    : t("Select Batches")}
                                </Button>
                              </Col>

                              <Col md={1}>
                                <Input
                                  type="number"
                                  value={item.price || 0}
                                  onChange={(e) => handleItemChange(index, "price", Number.parseFloat(e.target.value) || 0)}
                                />
                              </Col>
                              <Col md={2}>
                                <div className="discount-group d-flex align-items-center" style={{ gap: '6px' }}>
                                  <Input
                                    type="number"
                                    value={item.discount}
                                    className="flex-grow-1"
                                    onChange={(e) =>
                                      handleItemChange(
                                        index,
                                        "discount",
                                        Number.parseFloat(e.target.value)
                                      )
                                    }
                                  />
                                  <Input
                                    type="select"
                                    value={item.discountType || 'percent'}
                                    onChange={(e) => handleItemChange(index, 'discountType', e.target.value)}
                                    className="discount-type-select"
                                  >
                                    <option value="percent">%</option>
                                    <option value="amount">RM</option>
                                  </Input>
                                </div>
                              </Col>
                              <Col md={1}>
                                <span>{calculateItemTotal(item).toFixed(2)}</span>
                              </Col>




                              {/* Status */}
                              <Col md={1}>
                                {item.name && (
                                  <div className={`status-badge ${isQuantityMatched ? "complete" : "incomplete"}`}>
                                    {isQuantityMatched ? t("✅") : t("❌")}
                                  </div>
                                )}
                              </Col>

                              {/* Remove Item Button */}
                              <Col md={1}>
                                <Button
                                  color="light"
                                  className="remove-item-btn"
                                  onClick={() => handleRemoveItem(index)}
                                >
                                  <FaTrash className="text-danger" />
                                </Button>
                              </Col>
                            </Row>
                          </div>
                        )
                      })
                    )}
                  </div>



                  {/* Order Summary Section */}
                  <div className="order-summary mb-4">
                    <div className="p-3 border rounded">
                      <h5 className="mb-3 fw-bold">{t("Order Summary")}</h5>
                      <div className="d-flex justify-content-between mb-2">
                        <span>{t("Subtotal")}:</span>
                        <span>RM {subtotal.toFixed(2)}</span>
                      </div>

                      <hr />
                      <div className="d-flex justify-content-between fw-bold">
                        <span>{t("Total")}:</span>
                        <span>RM {totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>



                  <div className="d-flex align-items-center mb-3">
                    <Button type="button" color="primary" className="add-item-btn" onClick={handleAddItem}>
                      <FaPlus className="me-2" /> {t("Add New Item")}
                    </Button>
                    <Button type="submit" color="primary" style={{ marginLeft: '5px' }} className="submit-btn px-4 ml-3">
                      <FaCheck className="me-2" /> {t("Submit Order")}
                    </Button>
                  </div>


                </div>
              </LoadingOverlay>
            </Form>
          </div>
        </Card>
        <OrderConfirmationModal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} orderId={orderId} />

        {/* Batch Selection Modal */}
        {currentItemIndex !== null && (
          <BatchSelectionModal
            isOpen={batchModalOpen}
            toggle={() => setBatchModalOpen(!batchModalOpen)}
            availableBatches={items[currentItemIndex]?.availableBatches || []}
            selectedBatches={items[currentItemIndex]?.selectedBatches || []}
            onSave={handleBatchSelectionSave}
            totalQuantity={Number.parseInt(items[currentItemIndex]?.quantity) || 0}
          />
        )}

        {/* Item Picker Modal */}
        <ItemPickerModal
          isOpen={itemPickerOpen}
          toggle={() => setItemPickerOpen(false)}
          allItems={allItems}
          currentValue={itemPickerIndex !== null ? items[itemPickerIndex]?.name : null}
          onSelect={(selectedOption) => {
            if (itemPickerIndex !== null) {
              handleItemChange(itemPickerIndex, "name", selectedOption)
            }
          }}
        />
      </div>
      <style>{`
  .app-main__outer {
    background-color: #f8f9fa;
    min-height: 100vh;
    padding-top: 20px;
  }
  .rfq-card {
    border-radius: 10px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    border: none;
  }
  .form-select, .form-control {
    border-color: #e0e0e0;
    border-radius: 5px;
    padding: 10px;
  }
  .btn-light {
    background-color: #ffffff;
    border-color: #e0e0e0;
    color: #333;
    border-radius: 6px;
  }
  .btn-primary {
    background-color: #3D607A;
    border-color: #3D607A;
    border-radius: 6px;
  }
  .btn-primary:hover {
    background-color: #2d4a5c;
    border-color: #2d4a5c;
  }
  .btn-outline-primary {
    border-color: #3D607A;
    color: #3D607A;
    border-radius: 6px;
  }
  .btn-outline-primary:hover:not(:disabled) {
    background-color: #3D607A;
    border-color: #3D607A;
  }
  .btn-outline-success {
    border-color: #28a745;
    color: #28a745;
    border-radius: 6px;
  }
  .btn-outline-success:hover:not(:disabled) {
    background-color: #28a745;
    border-color: #28a745;
  }
  .btn-secondary {
    background-color: #6c757d;
    border-color: #6c757d;
    border-radius: 6px;
  }
  .btn-secondary:hover:not(:disabled) {
    background-color: #5a6268;
    border-color: #5a6268;
  }
  .btn-danger {
    background-color: #dc3545;
    border-color: #dc3545;
    border-radius: 6px;
  }
  .btn-danger:hover:not(:disabled) {
    background-color: #c82333;
    border-color: #c82333;
  }
  .alert-info {
    background-color: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
  }
  .alert-info .fas {
    color: #0c5460;
  }
  .gap-2 {
    gap: 0.5rem !important;
  }
  .text-muted {
    color: #6c757d !important;
  }
  h4, h5 {
    color: #333;
  }
  .item-list {
    border-top: 1px solid #e0e0e0;
    border-bottom: 1px solid #e0e0e0;
    padding: 20px 0;
    background-color: #fcfcfc;
    border-radius: 8px;
  }
  .order-confirmation-modal {
    display: flex !important;
    align-items: center;
    justify-content: center;
  }
  .order-confirmation-modal .modal-content {
    border-radius: 20px;
    border: none;
    box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  }
  .order-confirmation-modal .modal-body {
    padding: 2rem;
  }
  .confirmation-icon {
    width: 60px;
    height: 60px;
    background-color: #4CAF50;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1.5rem;
  }
  .order-confirmation-modal h4 {
    font-size: 1.25rem;
    color: #333;
    margin-bottom: 0.5rem;
  }
  .order-id {
    font-size: 1.5rem;
    font-weight: bold;
    color: #000;
  }
  .batch-required .css-1s2u09g-control {
    border-color: #dc3545;
  }
  /* New styles for enhanced design */
  .badge {
    padding: 6px 10px;
    border-radius: 20px;
    font-weight: 500;
    font-size: 0.75rem;
  }
  .badge.bg-success {
    background-color: #28a745 !important;
  }
  .badge.bg-danger {
    background-color: #dc3545 !important;
  }
  .small.mt-1 {
    background-color: #f8f9fa;
    border-radius: 6px;
    padding: 5px 8px;
    margin-top: 8px !important;
  }
  .small.mt-1 .text-muted {
    display: inline-block;
    background-color: rgba(61, 96, 122, 0.1);
    color: #3D607A !important;
    border-radius: 12px;
    padding: 2px 8px;
    margin: 2px;
    font-size: 0.8rem;
  }
  /* Batch selection modal styling */
  .batch-selection-modal .modal-content {
    border-radius: 12px;
    border: none;
  }
  .batch-table th {
    background-color: #f8f9fa;
    font-weight: 600;
  }
  .batch-table td {
    vertical-align: middle;
  }
  .quantity-input {
    border-radius: 6px;
    text-align: center;
  }
  /* Discount input sizing to ensure visibility and fit in one row */
  .discount-group input.form-control {
    min-width: 90px; /* ensure enough room to see typed numbers */
  }
  .discount-group .discount-type-select {
    width: 70px; /* fixed width for % / RM */
    min-width: 70px;
  }
  /* Row styling */
  .mb-2.align-items-center {
    background-color: white;
    border-radius: 8px;
    padding: 12px 8px;
    margin-bottom: 12px !important;
    box-shadow: 0 2px 5px rgba(0,0,0,0.03);
    transition: all 0.2s;
  }
  .mb-2.align-items-center:hover {
    box-shadow: 0 3px 10px rgba(0,0,0,0.08);
  }
  /* Section styling */
  .mb-3.fw-bold {
    border-left: 4px solid #3D607A;
    padding-left: 10px;
    margin-top: 20px;
  }
  .tooltip {
    font-size: 12px;
  }
  .tooltip-inner {
    max-width: 300px;
    text-align: left;
    background-color: #333;
    color: #fff;
    border-radius: 4px;
    padding: 8px 12px;
  }
  /* Dropdown wrapper — holds the Select and icon on the same line */
  .rfq-select-wrap {
    position: relative;
    display: flex;
    align-items: center;
  }
  /* Preview thumbnail — sits on the left edge of the dropdown control */
  .rfq-preview-icon-btn {
    position: absolute;
    left: 8px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border-radius: 8px;
    border: 1.5px solid #e0e0e0;
    background: #f5f7fa;
    overflow: hidden;
    cursor: pointer;
    transition: border-color 0.18s;
    padding: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .rfq-preview-icon-btn:hover {
    border-color: #3D607A;
  }
  .rfq-preview-icon-btn img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .rfq-preview-icon-btn i {
    font-size: 1rem;
    color: #bbb;
  }
  /* Floating preview popover — anchored to the icon via fixed coords */
  .rfq-preview-popover {
    position: fixed;
    z-index: 9999;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
    width: 190px;
    overflow: hidden;
  }
  .rfq-preview-popover__media {
    width: 100%;
    aspect-ratio: 1;
    background: linear-gradient(135deg,#f5f7fa,#e8ecf0);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .rfq-preview-popover__media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .rfq-preview-popover__media i {
    font-size: 2.5rem;
    color: #bbb;
  }
  .rfq-preview-popover__body {
    padding: 10px 12px 12px;
  }
  .rfq-preview-popover__title {
    margin: 0;
    font-weight: 600;
    font-size: 0.8rem;
    color: #333;
    line-height: 1.3;
  }
  .rfq-preview-popover__chips {
    display: flex;
    gap: 4px;
    margin-top: 6px;
    flex-wrap: wrap;
  }
  .rfq-preview-chip {
    font-size: 0.68rem;
    border-radius: 20px;
    padding: 2px 7px;
    font-weight: 500;
  }
  .rfq-preview-chip--code {
    background: #f0f4f7;
    color: #3D607A;
  }
  .rfq-preview-chip--uom {
    background: rgba(61,96,122,0.12);
    color: #3D607A;
  }
  .rfq-preview-popover__price {
    margin: 6px 0 0;
    font-size: 0.8rem;
    color: #2d7a4a;
    font-weight: 700;
  }
`}</style>
    </div>
  )
}

export default AddNewRFQ

